// A "project" (building) card in the grouped property view. Shows the project
// name, a few shared fields, and aggregate unit stats; clicking drills into
// that project's unit list (`?projectId=<id>`). An optional edit affordance
// (owner / manager / source) opens the project form dialog.

import { useNavigate, useSearchParams } from "react-router";
import { Building2, ChevronRight, Layers, Pencil } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatPrice } from "@/features/property/utils/format-price";
import type { Project } from "@/features/project/api";

export function ProjectCard({
  project,
  onEdit,
}: {
  project: Project;
  onEdit?: (() => void) | undefined;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const open = () => {
    const out = new URLSearchParams(searchParams);
    out.set("projectId", String(project.id));
    out.delete("group");
    out.delete("building");
    out.delete("page");
    navigate({ pathname: "/properties", search: `?${out.toString()}` });
  };

  const priceRange = formatProjectPriceRange(project);

  // A compact "shared fields" line so a project feels first-class even before
  // you drill in. Only the fields that are set show up.
  const meta = [
    project.completionYear != null ? `Bàn giao ${project.completionYear}` : null,
    project.totalFloors != null ? `${project.totalFloors} tầng` : null,
    project.basements != null ? `${project.basements} hầm` : null,
  ].filter((s): s is string => s !== null);

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`${project.name} — ${project.unitCount} căn`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className={cn(
        "group/card relative flex cursor-pointer flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-soft)] transition-all",
        "hover:border-border-strong hover:shadow-[var(--shadow-card)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 aria-hidden="true" className="size-4" />
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-[16px] font-bold tracking-tight">
              {project.name}
            </h3>
            <p className="font-mono text-[12px] text-muted-foreground">
              {project.unitCount} căn
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onEdit ? (
            <button
              type="button"
              aria-label={`Sửa dự án ${project.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none group-hover/card:opacity-100"
            >
              <Pencil aria-hidden="true" className="size-3.5" />
            </button>
          ) : null}
          <ChevronRight
            aria-hidden="true"
            className="mt-0.5 size-4 text-muted-foreground"
          />
        </div>
      </div>

      {meta.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-muted-foreground">
          <Layers aria-hidden="true" className="size-3.5" />
          {meta.map((m, i) => (
            <span key={m}>
              {i > 0 ? <span className="mr-2.5 text-border">·</span> : null}
              {m}
            </span>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        {project.saleCount > 0 ? <span>{project.saleCount} bán</span> : null}
        {project.rentCount > 0 ? <span>{project.rentCount} cho thuê</span> : null}
        <span className="font-medium text-emerald-600">
          {project.availableCount} còn trống
        </span>
      </div>

      <div className="mt-auto border-t border-border pt-2.5">
        <span className="font-mono text-[14px] font-semibold tabular-nums">
          {priceRange}
        </span>
      </div>
    </div>
  );
}

/** "3.2 tỷ – 5 tỷ", a single price, or "—" when the project has no units yet. */
export function formatProjectPriceRange(project: Project): string {
  if (project.minPrice == null || project.maxPrice == null) return "—";
  if (project.minPrice === project.maxPrice) {
    return formatPrice(project.minPrice, "SALE");
  }
  return `${formatPrice(project.minPrice, "SALE")} – ${formatPrice(project.maxPrice, "SALE")}`;
}
